'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

function formatIDR(amount: number): string {
  if (amount >= 1000000000) return `Rp ${(amount / 1000000000).toFixed(1)} M`;
  if (amount >= 1000000) return `Rp ${(amount / 1000000).toFixed(0)} Jt`;
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

interface FeaturedProject {
  id: string;
  name: string;
  location: string;
  city: string;
  total_plots: number;
  available_plots?: number;
  price_from: number | null;
  image_url: string | null;
  image_alt: string | null;
  legalitas: string | null;
  badge: string | null;
  status: string;
}

interface LatestKavling {
  id: string;
  name: string;
  project_id: string;
  project_name?: string;
  area_m2: number;
  price: number;
  status: string;
  image_url: string | null;
  image_alt: string | null;
}

// Fallback data shown only when Supabase is unavailable AND project is ACTIVE
const fallbackFeaturedProjects: FeaturedProject[] = [
  {
    id: 'arraya-singopadu', name: 'ARRAYA Singopadu', location: 'Exit Tol Pungkruk, Sragen',
    city: 'Sragen', total_plots: 7, available_plots: 1, price_from: 75000000,
    image_url: '/assets/images/image-1787233529086.png',
    image_alt: 'Siteplan ARRAYA Singopadu dekat Exit Tol Pungkruk Sragen',
    legalitas: 'SHM', badge: 'Baru', status: 'ACTIVE'
  }
];

const fallbackLatestKavling: LatestKavling[] = [
  { id: 'arraya-kav-3', name: 'KAV 3', project_id: 'arraya-singopadu', project_name: 'ARRAYA Singopadu', area_m2: 86, price: 75000000, status: 'AVAILABLE', image_url: '/assets/images/image-1787233529086.png', image_alt: 'Kavling 3 ARRAYA Singopadu 86 m² tersedia dekat Exit Tol Pungkruk Sragen' }
];

interface Testimonial {
  id: string;
  customer_name: string;
  property: string;
  rating: number;
  testimonial_text: string;
  photo_url: string | null;
  photo_alt: string | null;
  sort_order: number;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  sort_order: number;
}

interface BlogArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
  featured_image_alt: string | null;
  categories: string[];
  author_name: string;
  published_at: string | null;
}

const fallbackFaqs: FAQItem[] = [
  { id: '1', question: 'Apakah kavling sudah bersertifikat SHM?', answer: 'Ya, seluruh kavling BETALAND.ID memiliki sertifikat SHM (Sertifikat Hak Milik) yang sudah terdaftar di BPN.', category: 'Legalitas', sort_order: 1 },
  { id: '2', question: 'Berapa booking fee untuk memesan kavling?', answer: 'Booking fee mulai dari Rp 5.000.000 untuk mengamankan kavling pilihan Anda. Booking fee dapat diperhitungkan sebagai bagian dari pembayaran.', category: 'Pembelian', sort_order: 2 },
  { id: '3', question: 'Apakah bisa dicicil tanpa bunga?', answer: 'Tersedia skema cicilan langsung ke developer dengan tenor 12-36 bulan. Beberapa unit tersedia dengan program cicilan 0% untuk periode tertentu.', category: 'Pembayaran', sort_order: 3 },
  { id: '4', question: 'Bagaimana proses survey lokasi?', answer: 'Tim kami akan menjadwalkan kunjungan ke lokasi sesuai waktu yang Anda inginkan. Survey gratis tanpa syarat.', category: 'Umum', sort_order: 4 },
  { id: '5', question: 'Apakah bisa memilih nomor kavling sendiri?', answer: 'Ya, Anda dapat memilih nomor kavling yang tersedia melalui siteplan interaktif di website kami.', category: 'Umum', sort_order: 5 },
];

const faqs = [
  { q: 'Apakah kavling sudah bersertifikat SHM?', a: 'Ya, seluruh kavling BETALAND.ID memiliki sertifikat SHM (Sertifikat Hak Milik) yang sudah terdaftar di BPN.' },
  { q: 'Berapa booking fee untuk memesan kavling?', a: 'Booking fee mulai dari Rp 5.000.000 untuk mengamankan kavling pilihan Anda. Booking fee dapat diperhitungkan sebagai bagian dari pembayaran.' },
  { q: 'Apakah bisa dicicil tanpa bunga?', a: 'Tersedia skema cicilan langsung ke developer dengan tenor 12-36 bulan. Beberapa unit tersedia dengan program cicilan 0% untuk periode tertentu.' },
  { q: 'Bagaimana proses survey lokasi?', a: 'Tim kami akan menjadwalkan kunjungan ke lokasi sesuai waktu yang Anda inginkan. Survey gratis tanpa syarat.' },
  { q: 'Apakah bisa memilih nomor kavling sendiri?', a: 'Ya, Anda dapat memilih nomor kavling yang tersedia melalui siteplan interaktif di website kami.' }
];

const buyingSteps = [
  { step: 1, title: 'Pilih Kavling', desc: 'Temukan kavling impian Anda melalui marketplace atau siteplan interaktif', icon: 'MagnifyingGlassIcon' },
  { step: 2, title: 'Konsultasi', desc: 'Hubungi tim kami via WhatsApp untuk informasi lengkap', icon: 'ChatBubbleLeftRightIcon' },
  { step: 3, title: 'Survey Lokasi', desc: 'Kunjungi lokasi kavling bersama tim profesional kami', icon: 'MapPinIcon' },
  { step: 4, title: 'Booking', desc: 'Amankan kavling pilihan dengan booking fee', icon: 'DocumentCheckIcon' },
  { step: 5, title: 'Pembayaran', desc: 'Pilih skema pembayaran: tunai, cicilan, atau KPR', icon: 'CreditCardIcon' },
  { step: 6, title: 'Legalitas', desc: 'Proses balik nama dan serah terima dokumen SHM', icon: 'ShieldCheckIcon' }
];

const keunggulan = [
  { title: 'Legalitas Terjamin', desc: 'Semua kavling bersertifikat SHM, terdaftar resmi di BPN', icon: 'ShieldCheckIcon', color: 'bg-primary/10 text-primary' },
  { title: 'Lokasi Strategis', desc: 'Kawasan berkembang dengan akses infrastruktur yang terus meningkat', icon: 'MapPinIcon', color: 'bg-accent/10 text-accent' },
  { title: 'Harga Transparan', desc: 'Tidak ada biaya tersembunyi. Semua biaya tertera jelas sejak awal', icon: 'CurrencyDollarIcon', color: 'bg-secondary/10 text-secondary' },
  { title: 'Proses Mudah', desc: 'Dari pilih kavling hingga AJB, kami dampingi setiap langkahnya', icon: 'CheckCircleIcon', color: 'bg-blue/10 text-blue' }
];

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [refCode, setRefCode] = useState<string | null>(null);
  const [featuredProjects, setFeaturedProjects] = useState<FeaturedProject[]>(fallbackFeaturedProjects);
  const [latestKavling, setLatestKavling] = useState<LatestKavling[]>(fallbackLatestKavling);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [realtimeIndicator, setRealtimeIndicator] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [dynamicFaqs, setDynamicFaqs] = useState<FAQItem[]>([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [blogArticles, setBlogArticles] = useState<BlogArticle[]>([]);
  const [loadingBlog, setLoadingBlog] = useState(true);
  const [featuredKavling, setFeaturedKavling] = useState<LatestKavling[]>([]);
  const [loadingFeaturedKavling, setLoadingFeaturedKavling] = useState(true);

  // Hero CMS config
  const [heroConfig, setHeroConfig] = useState({
    badge: 'Platform Kavling Terpercaya #1 di Bogor',
    headline: 'Temukan Kavling Impian Anda Bersama BETALAND.ID',
    subheadline: 'Investasi properti terpercaya di kawasan premium Bogor & Puncak',
    cta_label: 'Lihat Kavling',
    cta_url: '/kavling',
    bg_image: '',
  });

  // Live stats from DB
  const [liveStats, setLiveStats] = useState({
    totalKavling: 0,
    availableKavling: 0,
    activeProjects: 0,
    satisfiedBuyers: 500,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setRefCode(ref);
      sessionStorage.setItem('betaland_ref', ref);
      sessionStorage.setItem('betaland_ref_source', 'homepage');
    }
  }, []);

  // Fetch hero config from homepage_config table
  useEffect(() => {
    const supabase = createClient();
    const fetchHeroConfig = async () => {
      const { data, error } = await supabase
        .from('homepage_config')
        .select('config')
        .eq('section_key', 'hero')
        .single();
      if (!error && data?.config) {
        const cfg = data.config as Record<string, any>;
        setHeroConfig({
          badge: cfg.badge || 'Platform Kavling Terpercaya #1 di Bogor',
          headline: cfg.headline || 'Temukan Kavling Impian Anda Bersama BETALAND.ID',
          subheadline: cfg.subheadline || 'Investasi properti terpercaya di kawasan premium Bogor & Puncak',
          cta_label: cfg.cta_label || 'Lihat Kavling',
          cta_url: cfg.cta_url || '/kavling',
          bg_image: cfg.bg_image || '',
        });
      }
    };
    fetchHeroConfig();

    // Real-time: re-fetch hero config when homepage_config changes
    const heroChannel = supabase
      .channel('homepage_hero_config_realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'homepage_config' }, () => {
        fetchHeroConfig();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(heroChannel);
    };
  }, []);

  // Fetch live stats from DB
  useEffect(() => {
    const supabase = createClient();
    const fetchStats = async () => {
      const [totalRes, availRes, activeRes] = await Promise.all([
        supabase.from('plots').select('*', { count: 'exact', head: true }),
        supabase.from('plots').select('*', { count: 'exact', head: true }).eq('status', 'AVAILABLE'),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'ACTIVE'),
      ]);
      setLiveStats({
        totalKavling: totalRes.count ?? 0,
        availableKavling: availRes.count ?? 0,
        activeProjects: activeRes.count ?? 0,
        satisfiedBuyers: 500,
      });
    };
    fetchStats();

    // Re-fetch stats when plots or projects change
    const statsChannel = supabase
      .channel('homepage_stats_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'plots' }, fetchStats)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, fetchStats)
      .subscribe();

    return () => {
      supabase.removeChannel(statsChannel);
    };
  }, []);

  useEffect(() => {
    const supabase = createClient();

    const fetchData = async () => {
      setLoadingProjects(true);

      // Fetch only ACTIVE projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('id, name, location, city, total_plots, price_from, image_url, image_alt, legalitas, badge, status')
        .eq('status', 'ACTIVE')
        .order('created_at', { ascending: false });

      if (!projectsError && projectsData && projectsData.length > 0) {
        // For each active project, count available plots
        const projectsWithCounts = await Promise.all(
          projectsData.map(async (proj) => {
            const { count } = await supabase
              .from('plots')
              .select('*', { count: 'exact', head: true })
              .eq('project_id', proj.id)
              .eq('status', 'AVAILABLE');
            return { ...proj, available_plots: count || 0 };
          })
        );
        setFeaturedProjects(projectsWithCounts as FeaturedProject[]);
      }

      // Fetch latest AVAILABLE kavling from ACTIVE projects only
      const { data: kavlingData, error: kavlingError } = await supabase
        .from('plots')
        .select(`
          id, name, project_id, area_m2, price, status, image_url, image_alt,
          projects!inner(name, status)
        `)
        .eq('status', 'AVAILABLE')
        .eq('projects.status', 'ACTIVE')
        .order('created_at', { ascending: false })
        .limit(4);

      if (!kavlingError && kavlingData && kavlingData.length > 0) {
        const mapped = kavlingData.map((k: any) => ({
          id: k.id,
          name: k.name,
          project_id: k.project_id,
          project_name: k.projects?.name || '',
          area_m2: k.area_m2,
          price: k.price,
          status: k.status,
          image_url: k.image_url,
          image_alt: k.image_alt,
        }));
        setLatestKavling(mapped);
      }

      setLoadingProjects(false);
    };

    fetchData();

    // Real-time: when a project status changes, refetch
    const projectChannel = supabase
      .channel('homepage_projects_realtime')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects' },
        () => {
          fetchData();
          setRealtimeIndicator(true);
          setTimeout(() => setRealtimeIndicator(false), 3000);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'projects' },
        () => { fetchData(); }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'plots' },
        () => {
          fetchData();
          setRealtimeIndicator(true);
          setTimeout(() => setRealtimeIndicator(false), 3000);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'promo_campaigns' },
        () => {
          // When a promo campaign activates or expires, refetch so kavling promo badges update
          fetchData();
          setRealtimeIndicator(true);
          setTimeout(() => setRealtimeIndicator(false), 3000);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(projectChannel);
    };
  }, []);

  // Fetch featured kavling from featured_items table with real-time sync
  useEffect(() => {
    const supabase = createClient();

    const fetchFeaturedKavling = async () => {
      setLoadingFeaturedKavling(true);
      const { data: featData, error: featError } = await supabase
        .from('featured_items')
        .select('item_id, sort_order')
        .eq('item_type', 'kavling')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (!featError && featData && featData.length > 0) {
        const plotIds = featData.map((f: any) => f.item_id);
        const { data: plotData, error: plotError } = await supabase
          .from('plots')
          .select('id, plot_number, block, project_id, area_m2, price, status, image_url, image_alt, projects!inner(name, status)')
          .in('id', plotIds);

        if (!plotError && plotData) {
          const sortMap = new Map(featData.map((f: any) => [f.item_id, f.sort_order]));
          const mapped: LatestKavling[] = plotData
            .map((k: any) => ({
              id: k.id,
              name: `${k.block ? k.block + '-' : ''}${k.plot_number}`,
              project_id: k.project_id,
              project_name: k.projects?.name || '',
              area_m2: k.area_m2,
              price: k.price,
              status: k.status,
              image_url: k.image_url,
              image_alt: k.image_alt,
              _sort: sortMap.get(k.id) ?? 999,
            }))
            .sort((a: any, b: any) => a._sort - b._sort);
          setFeaturedKavling(mapped);
        }
      } else {
        setFeaturedKavling([]);
      }
      setLoadingFeaturedKavling(false);
    };

    fetchFeaturedKavling();

    const featuredKavlingChannel = supabase
      .channel('homepage_featured_kavling_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'featured_items' }, () => {
        fetchFeaturedKavling();
        setRealtimeIndicator(true);
        setTimeout(() => setRealtimeIndicator(false), 3000);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'plots' }, () => {
        fetchFeaturedKavling();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(featuredKavlingChannel);
    };
  }, []);

  // Fetch published testimonials with real-time sync
  useEffect(() => {
    const supabase = createClient();

    const fetchTestimonials = async () => {
      setLoadingTestimonials(true);
      const { data, error } = await supabase
        .from('testimonials')
        .select('id, customer_name, property, rating, testimonial_text, photo_url, photo_alt, sort_order')
        .eq('publish_status', 'published')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (!error && data) {
        setTestimonials(data as Testimonial[]);
      }
      setLoadingTestimonials(false);
    };

    fetchTestimonials();

    const testimonialChannel = supabase
      .channel('homepage_testimonials_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'testimonials' }, () => {
        fetchTestimonials();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(testimonialChannel);
    };
  }, []);

  // Fetch published FAQs with real-time sync
  useEffect(() => {
    const supabase = createClient();

    const fetchFaqs = async () => {
      setLoadingFaqs(true);
      const { data, error } = await supabase
        .from('faqs')
        .select('id, question, answer, category, sort_order')
        .eq('publish_status', 'published')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (!error && data && data.length > 0) {
        setDynamicFaqs(data as FAQItem[]);
      } else {
        setDynamicFaqs(fallbackFaqs);
      }
      setLoadingFaqs(false);
    };

    fetchFaqs();

    const faqChannel = supabase
      .channel('homepage_faqs_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'faqs' }, () => {
        fetchFaqs();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(faqChannel);
    };
  }, []);

  // Fetch published blog articles with real-time sync
  useEffect(() => {
    const supabase = createClient();

    const fetchBlog = async () => {
      setLoadingBlog(true);
      const { data, error } = await supabase
        .from('blog_articles')
        .select('id, title, slug, excerpt, featured_image_url, featured_image_alt, categories, author_name, published_at')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(3);
      if (!error && data) {
        setBlogArticles(data as BlogArticle[]);
      }
      setLoadingBlog(false);
    };

    fetchBlog();

    const blogChannel = supabase
      .channel('homepage_blog_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'blog_articles' }, () => {
        fetchBlog();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(blogChannel);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const ref = sessionStorage.getItem('betaland_ref');
    const query = searchQuery ? `?q=${encodeURIComponent(searchQuery)}${ref ? `&ref=${ref}` : ''}` : ref ? `?ref=${ref}` : '';
    window.location.href = `/kavling${query}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* HERO */}
      <section
        className="relative overflow-hidden min-h-[600px] flex items-center"
        style={heroConfig.bg_image
          ? { backgroundImage: `url(${heroConfig.bg_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { background: 'linear-gradient(135deg, #0a2e1e 0%, #1a5c3a 50%, #0a2e1e 100%)' }
        }
      >
        {heroConfig.bg_image && <div className="absolute inset-0 bg-black/50" />}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-secondary blur-2xl" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            {heroConfig.badge && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/20 border border-accent/30 rounded-full text-accent text-xs font-semibold mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                {heroConfig.badge}
              </div>
            )}
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
              {heroConfig.headline}
            </h1>
            <p className="text-lg text-white/70 mb-8 max-w-lg">
              {heroConfig.subheadline}
            </p>
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Cari kavling, proyek, atau lokasi..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3.5 bg-white rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 shadow-lg"
                />
              </div>
              <button type="submit" className="px-6 py-3.5 bg-accent text-accent-foreground font-bold rounded-xl hover:bg-accent/90 transition-colors shadow-lg whitespace-nowrap">
                Cari Kavling
              </button>
            </form>
            <div className="flex flex-wrap gap-4 mt-6 text-sm text-white/60">
              <span>🟢 {liveStats.availableKavling}+ Kavling Tersedia</span>
              <span>📍 3 Lokasi Strategis</span>
              <span>✅ SHM Terjamin</span>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_18c2d4ebd-1781366777030.png"
                alt="Kawasan kavling hijau premium BETALAND.ID"
                width={600} height={420}
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Kavling Terbaru</p>
                    <p className="font-bold text-foreground">Green Valley — A12</p>
                    <p className="text-sm text-primary font-semibold">Rp 180 Jt · 120 m²</p>
                  </div>
                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-bold rounded-lg">🟢 Tersedia</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-card border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Total Kavling', value: liveStats.totalKavling > 0 ? `${liveStats.totalKavling}` : '260+', icon: 'HomeIcon' },
              { label: 'Kavling Tersedia', value: liveStats.availableKavling > 0 ? `${liveStats.availableKavling}` : '120+', icon: 'CheckCircleIcon' },
              { label: 'Proyek Aktif', value: liveStats.activeProjects > 0 ? `${liveStats.activeProjects}` : '3', icon: 'BuildingOfficeIcon' },
              { label: 'Pembeli Puas', value: liveStats.satisfiedBuyers > 0 ? `${liveStats.satisfiedBuyers}` : '500+', icon: 'StarIcon' }
            ].map((stat) =>
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={stat.icon as any} size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS — only ACTIVE */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <p className="text-sm font-semibold text-accent uppercase tracking-wider">Proyek Unggulan</p>
              {realtimeIndicator && (
                <span className="flex items-center gap-1 text-xs text-secondary font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-ping" />
                  Diperbarui
                </span>
              )}
            </div>
            <h2 className="text-3xl font-bold text-foreground">Pilihan Terbaik BETALAND.ID</h2>
          </div>
          <Link href="/projects" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-dark transition-colors">
            Lihat Semua <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        {loadingProjects ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-2xl overflow-hidden border border-border animate-pulse">
                <div className="h-52 bg-muted" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : featuredProjects.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="BuildingOfficeIcon" size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Belum ada proyek aktif saat ini</p>
            <p className="text-sm mt-1">Silakan cek kembali nanti</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {featuredProjects.map((proj) =>
              <Link key={proj.id} href={`/projects/${proj.id}`} className="group bg-card rounded-2xl overflow-hidden border border-border shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1">
                <div className="relative h-52 overflow-hidden">
                  <AppImage
                    src={proj.image_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_146eae9b0-1787210095043.png'}
                    alt={proj.image_alt || proj.name}
                    width={400} height={208}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to from-black/60 to-transparent" />
                  {proj.badge && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-full">{proj.badge}</span>
                  )}
                  {proj.legalitas && (
                    <span className="absolute top-3 right-3 px-2.5 py-1 bg-white/90 text-primary text-xs font-bold rounded-full">{proj.legalitas}</span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-foreground text-lg mb-1">{proj.name}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-4">
                    <Icon name="MapPinIcon" size={14} /> {proj.location}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground">Harga mulai</p>
                      <p className="font-bold text-primary">{formatIDR(proj.price_from)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{proj.available_plots ?? '—'} tersedia</p>
                      <p className="text-xs text-muted-foreground">dari {proj.total_plots} kavling</p>
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}

        <div className="text-center mt-6 sm:hidden">
          <Link href="/projects" className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
            Lihat Semua Proyek <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </section>

      {/* LATEST KAVLING — only from ACTIVE projects */}
      <section className="bg-muted/50 py-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <p className="text-sm font-semibold text-accent uppercase tracking-wider">Kavling Pilihan</p>
                {realtimeIndicator && (
                  <span className="flex items-center gap-1 text-xs text-secondary font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-ping" />
                    Diperbarui
                  </span>
                )}
              </div>
              <h2 className="text-3xl font-bold text-foreground">Kavling Unggulan Kami</h2>
            </div>
            <Link href="/kavling" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-dark transition-colors">
              Lihat Semua <Icon name="ArrowRightIcon" size={16} />
            </Link>
          </div>

          {loadingFeaturedKavling ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-card rounded-2xl overflow-hidden border border-border animate-pulse">
                  <div className="h-40 bg-muted" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredKavling.length === 0 ? (
            // Fallback to latestKavling if no featured kavling configured
            latestKavling.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <p className="text-sm">Belum ada kavling tersedia saat ini</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {latestKavling.map((kav) =>
                  <Link key={kav.id} href={`/kavling/${kav.id}`} className="group bg-card rounded-2xl overflow-hidden border border-border shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-40 overflow-hidden">
                      <AppImage
                        src={kav.image_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1ad9211d0-1784190870841.png'}
                        alt={kav.image_alt || kav.name}
                        width={300} height={160}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary/90 text-white text-xs font-bold rounded-full">🟢 Tersedia</span>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">{kav.project_name}</p>
                      <p className="font-bold text-foreground mb-2">{kav.name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{kav.area_m2} m²</span>
                        <span className="font-bold text-primary text-sm">{formatIDR(kav.price)}</span>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            )
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredKavling.map((kav) =>
                <Link key={kav.id} href={`/kavling/${kav.id}`} className="group bg-card rounded-2xl overflow-hidden border border-border shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1">
                  <div className="relative h-40 overflow-hidden">
                    <AppImage
                      src={kav.image_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1ad9211d0-1784190870841.png'}
                      alt={kav.image_alt || kav.name}
                      width={300} height={160}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className={`absolute top-2 left-2 px-2 py-0.5 text-white text-xs font-bold rounded-full ${kav.status === 'AVAILABLE' ? 'bg-secondary/90' : kav.status === 'HOLD' ? 'bg-orange/90' : 'bg-primary/90'}`}>
                      {kav.status === 'AVAILABLE' ? '🟢 Tersedia' : kav.status === 'HOLD' ? '🟡 Hold' : kav.status}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-muted-foreground mb-1">{kav.project_name}</p>
                    <p className="font-bold text-foreground mb-2">{kav.name}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{kav.area_m2} m²</span>
                      <span className="font-bold text-primary text-sm">{formatIDR(kav.price)}</span>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* KEUNGGULAN */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Mengapa BETALAND.ID</p>
          <h2 className="text-3xl font-bold text-foreground">Investasi Aman, Proses Mudah</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {keunggulan.map((item) =>
            <div key={item.title} className="bg-card rounded-2xl p-6 border border-border shadow-card">
              <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4`}>
                <Icon name={item.icon as any} size={24} />
              </div>
              <h3 className="font-bold text-foreground mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          )}
        </div>
      </section>

      {/* CARA MEMBELI */}
      <section className="bg-gradient-to-br from-primary/5 to-secondary/5 py-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Cara Membeli</p>
            <h2 className="text-3xl font-bold text-foreground">6 Langkah Mudah Miliki Kavling</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {buyingSteps.map((step) =>
              <div key={step.step} className="bg-card rounded-2xl p-6 border border-border shadow-card flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {step.step}
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </div>
              </div>
            )}
          </div>
          <div className="text-center mt-8">
            <Link href="/cara-membeli" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-primary text-primary font-semibold rounded-xl hover:bg-primary hover:text-white transition-colors">
              Pelajari Lebih Lanjut <Icon name="ArrowRightIcon" size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONI */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Testimoni</p>
          <h2 className="text-3xl font-bold text-foreground">Kata Mereka yang Sudah Memiliki</h2>
        </div>
        {loadingTestimonials ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-2xl p-6 border border-border animate-pulse">
                <div className="flex gap-1 mb-4">{[1,2,3,4,5].map((s) => <div key={s} className="w-4 h-4 rounded bg-muted" />)}</div>
                <div className="space-y-2 mb-5">
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-4/5" />
                  <div className="h-3 bg-muted rounded w-3/5" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted" />
                  <div className="space-y-1">
                    <div className="h-3 bg-muted rounded w-24" />
                    <div className="h-2 bg-muted rounded w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : testimonials.length === 0 ? null : (
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) =>
              <div key={t.id} className="bg-card rounded-2xl p-6 border border-border shadow-card">
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((i) => <Icon key={i} name="StarIcon" size={16} className={i <= t.rating ? 'text-accent' : 'text-muted'} />)}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 italic">"{t.testimonial_text}"</p>
                <div className="flex items-center gap-3">
                  {t.photo_url ? (
                    <AppImage src={t.photo_url} alt={t.photo_alt || `Foto ${t.customer_name}`} width={40} height={40} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-sm">{t.customer_name.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-foreground text-sm">{t.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{t.property}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* INSIGHT & BLOG */}
      <section className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Insight & Blog</p>
            <h2 className="text-3xl font-bold text-foreground">Tips & Informasi Properti</h2>
          </div>
          <Link href="/blog" className="hidden sm:flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            Lihat Semua <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
        {loadingBlog ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden animate-pulse">
                <div className="h-44 bg-muted" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-muted rounded w-1/3" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-4/5" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : blogArticles.length === 0 ? null : (
          <div className="grid md:grid-cols-3 gap-6">
            {blogArticles.map((article) => (
              <div key={article.id} className="bg-card rounded-2xl border border-border overflow-hidden shadow-card hover:shadow-lg transition-shadow group">
                {/* Featured image */}
                <div className="h-44 bg-muted overflow-hidden">
                  {article.featured_image_url ? (
                    <AppImage
                      src={article.featured_image_url}
                      alt={article.featured_image_alt || article.title}
                      width={400}
                      height={176}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/5">
                      <Icon name="NewspaperIcon" size={32} className="text-primary/30" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  {/* Categories */}
                  {article.categories && article.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {article.categories.slice(0, 2).map((cat) => (
                        <span key={cat} className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-semibold">{cat}</span>
                      ))}
                    </div>
                  )}
                  <h3 className="font-bold text-foreground leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors">{article.title}</h3>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-4">{article.excerpt}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="UserIcon" size={12} />
                      {article.author_name}
                    </span>
                    {article.published_at && (
                      <span className="flex items-center gap-1">
                        <Icon name="CalendarIcon" size={12} />
                        {new Date(article.published_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-center mt-6 sm:hidden">
          <Link href="/blog" className="text-sm font-semibold text-primary hover:underline">Lihat Semua Artikel →</Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/50 py-16">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">FAQ</p>
            <h2 className="text-3xl font-bold text-foreground">Pertanyaan yang Sering Ditanyakan</h2>
          </div>
          <div className="space-y-3">
            {dynamicFaqs.map((faq, i) =>
              <div key={i} className="bg-card rounded-xl border border-border overflow-hidden">
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-foreground hover:bg-muted/50 transition-colors"
                >
                  <span>{faq.question}</span>
                  <Icon name={activeFaq === i ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={18} className="text-muted-foreground flex-shrink-0 ml-4" />
                </button>
                {activeFaq === i &&
                  <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
                    {faq.answer}
                  </div>
                }
              </div>
            )}
          </div>
          <div className="text-center mt-6">
            <Link href="/faq" className="text-sm font-semibold text-primary hover:underline">Lihat Semua FAQ →</Link>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="bg-gradient-to-br from-primary to-primary-dark py-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Siap Miliki Kavling Impian?</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">Hubungi tim kami sekarang atau jelajahi semua kavling yang tersedia. Konsultasi gratis, tanpa syarat.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/6281282760110?text=Halo%20BETALAND.ID%2C%20saya%20ingin%20konsultasi%20mengenai%20kavling%20yang%20tersedia."
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#1ebe5d] transition-colors shadow-lg"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              Chat WhatsApp Sekarang
            </a>
            <Link href="/kavling" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary font-bold rounded-xl hover:bg-white/90 transition-colors shadow-lg">
              <Icon name="Squares2X2Icon" size={20} />
              Lihat Semua Kavling
            </Link>
          </div>
        </div>
      </section>

      {/* STICKY WHATSAPP MOBILE */}
      <div className="fixed bottom-4 right-4 z-50 md:hidden">
        <a
          href="https://wa.me/6281282760110?text=Halo%20BETALAND.ID%2C%20saya%20ingin%20informasi%20kavling."
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-3 bg-[#25D366] text-white font-bold rounded-full shadow-xl hover:bg-[#1ebe5d] transition-colors"
        >
          <Icon name="ChatBubbleLeftRightIcon" size={20} />
          <span className="text-sm">WhatsApp</span>
        </a>
      </div>

      <PublicFooter />
    </div>
  );
}